import { useState, useEffect } from 'react';
import {
  Phone, CheckCircle, Clock, Calendar, MessageSquare, Users,
  ArrowRight, Star, Shield, Zap, Globe, Building2, ThumbsUp,
  Smartphone, MapPin, Wrench, ChevronUp, ChevronDown
} from 'lucide-react';

export default function HomePage() {
  const [isStrategyFormOpen, setIsStrategyFormOpen] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [strategySubmitStatus, setStrategySubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactSubmitStatus, setContactSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [strategyFormData, setStrategyFormData] = useState({
    name: '', email: '', phone: '', company: '', revenue: '', message: ''
  });
  const [contactFormData, setContactFormData] = useState({
    name: '', email: '', phone: '', message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStrategySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    Object.entries(strategyFormData).forEach(([key, value]) => formData.append(key, value));
    try {
      const response = await fetch('https://readdy.ai/api/form/d404a55jp3ptvia83ic0', {
        method: 'POST', body: formData
      });
      if (response.ok) {
        setStrategySubmitStatus('success');
        setStrategyFormData({ name: '', email: '', phone: '', company: '', revenue: '', message: '' });
        setTimeout(() => { setIsStrategyFormOpen(false); setStrategySubmitStatus('idle'); }, 2500);
      } else { setStrategySubmitStatus('error'); }
    } catch { setStrategySubmitStatus('error'); }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    Object.entries(contactFormData).forEach(([key, value]) => formData.append(key, value));
    try {
      const response = await fetch('https://readdy.ai/api/form/d404a55jp3ptvia83icg', {
        method: 'POST', body: formData
      });
      if (response.ok) {
        setContactSubmitStatus('success');
        setContactFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => { setIsContactFormOpen(false); setContactSubmitStatus('idle'); }, 2500);
      } else { setContactSubmitStatus('error'); }
    } catch { setContactSubmitStatus('error'); }
  };

  const faqs = [
    {
      question: 'How does the AI know the difference between an HVAC emergency and a routine maintenance call?',
      answer: 'Our AI is specifically trained on HVAC and plumbing terminology and scenarios. It recognizes keywords and urgency indicators — like "no heat," "burst pipe," "gas smell," or "flooding" — and automatically prioritizes those calls, dispatching emergency notifications to you immediately while scheduling routine calls for the next available slot.'
    },
    {
      question: 'Will the AI work with my existing software like ServiceTitan or Housecall Pro?',
      answer: 'Yes! Our Basic CRM Sync integrates with ServiceTitan, Housecall Pro, and Jobber. Leads captured by the AI are automatically entered into your CRM — no double data entry. Appointments sync across platforms so your schedule stays up to date.'
    },
    {
      question: 'How does the bilingual support work for my Spanish-speaking customers?',
      answer: 'The AI automatically detects whether a caller is speaking English or Spanish and responds in their language. This applies to both phone calls and SMS messages. In Houston, nearly 40% of residents speak Spanish at home — so you\'ll never lose another lead due to a language barrier.'
    },
    {
      question: 'What happens during a power outage or if my internet goes down?',
      answer: 'Our AI runs in the cloud, completely independent of your local internet or power. Even if Houston gets hit by a storm and your office loses power, every call is still answered, every lead is captured, and you get notified via text. You never miss a call — period.'
    },
    {
      question: 'How quickly can I get set up and start taking calls?',
      answer: 'Most Houston contractors are fully live within 48 hours. We handle the setup, train the AI on your specific business (services offered, service area, pricing guidelines), and port or forward your number. You\'ll do a test run before going live to make sure everything sounds perfect.'
    },
    {
      question: 'Can I try it before committing to a monthly plan?',
      answer: 'Absolutely. Book a free 30-minute strategy call and we\'ll show you a live demo customized to your HVAC or plumbing business. You\'ll hear exactly how the AI handles your types of calls — emergencies, scheduling, follow-ups — before you spend a dime.'
    }
  ];

  const navLinks = [
    { label: 'Services', href: '#services' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Reviews', href: '#testimonials' },
    { label: 'Service Area', href: '#service-area' },
    { label: 'FAQ', href: '#faq' },
  ];

  return (
    <div className="min-h-screen scroll-smooth" style={{ fontFamily: '\'DM Sans\', sans-serif' }}>

      {/* ── NAV ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-6 md:px-12 transition-all duration-300"
        style={{
          padding: '16px 48px',
          background: isScrolled ? 'rgba(28,43,53,0.97)' : 'rgba(28,43,53,0.82)',
          backdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded"
            style={{
              width: 32, height: 32,
              background: 'rgba(30,203,181,0.12)',
              border: '1px solid rgba(30,203,181,0.28)',
            }}
          >
            <Phone className="w-4 h-4" style={{ color: '#1ECBB5' }} />
          </div>
          <span
            className="tracking-widest text-white text-xl select-none"
            style={{ fontFamily: '\'Bebas Neue\', sans-serif', letterSpacing: '0.1em' }}
          >
            Growth<span style={{ color: '#1ECBB5' }}>Key</span> AI Systems
          </span>
        </div>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-7">
          {navLinks.map(l => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm transition-colors"
              style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 300 }}
              onMouseEnter={e => (e.currentTarget.style.color = '#1ECBB5')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.65)')}
            >
              {l.label}
            </a>
          ))}
          <a
            href="tel:8326574587"
            className="hidden lg:flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:text-teal-400"
            style={{ color: 'rgba(255,255,255,0.75)' }}
          >
            <Phone className="w-4 h-4" style={{ color: '#1ECBB5' }} />
            (832) 847-6474
          </a>
          <a
            href="https://calendly.com/growthkeyaisystems-support/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-bold tracking-wide rounded whitespace-nowrap cursor-pointer transition-colors"
            style={{
              background: '#1ECBB5', color: '#1C2B35',
              padding: '10px 20px', borderRadius: 6, letterSpacing: '0.02em',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#17A090'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#1ECBB5'; }}
          >
            Get Started
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden flex flex-col gap-1.5 cursor-pointer"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="w-6 h-0.5 bg-white block" />
          <span className="w-6 h-0.5 bg-white block" />
          <span className="w-6 h-0.5 bg-white block" />
        </button>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="absolute top-full left-0 right-0 flex flex-col px-6 py-4 gap-4 md:hidden"
            style={{ background: 'rgba(28,43,53,0.98)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
          >
            {navLinks.map(l => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-sm py-1"
                style={{ color: 'rgba(255,255,255,0.75)' }}
              >
                {l.label}
              </a>
            ))}
            <a
              href="https://calendly.com/growthkeyaisystems-support/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-bold text-center rounded py-3 whitespace-nowrap"
              style={{ background: '#1ECBB5', color: '#1C2B35' }}
            >
              Get Started
            </a>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section
        className="relative min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.82), rgba(15,23,42,0.88)), url('https://public.readdy.ai/ai/img_res/79bb840444f5ca916415a47c82894059.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Grid overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(30,203,181,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(30,203,181,0.025) 1px,transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-12 pt-32 pb-20 grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            {/* Live badge */}
            <div
              className="inline-flex items-center gap-2 mb-3 text-xs tracking-widest"
              style={{
                fontFamily: '\'DM Mono\', monospace',
                color: '#1ECBB5',
                background: 'rgba(30,203,181,0.12)',
                border: '1px solid rgba(30,203,181,0.28)',
                padding: '5px 14px',
                borderRadius: 4,
              }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: '#1ECBB5', animation: 'pulse 2s ease-in-out infinite' }}
              />
              Growth Key AI Operating System
            </div>

            {/* Location pill */}
            <div
              className="inline-flex items-center gap-1.5 mb-6 text-xs"
              style={{
                color: '#1ECBB5',
                background: 'rgba(30,203,181,0.12)',
                border: '1px solid rgba(30,203,181,0.28)',
                padding: '4px 12px',
                borderRadius: 9999,
              }}
            >
              <MapPin className="w-3 h-3" style={{ color: '#1ECBB5' }} />
              Proudly Serving Houston &amp; Harris County
            </div>

            <h1
              className="mb-6 text-white leading-none"
              style={{
                fontFamily: '\'Bebas Neue\', sans-serif',
                fontSize: 'clamp(56px,7vw,88px)',
                letterSpacing: '0.01em',
                lineHeight: 0.92,
              }}
            >
              NEVER MISS<br />ANOTHER<br />SERVICE CALL
            </h1>

            <p className="text-xl mb-3" style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 300, maxWidth: 520 }}>
              The AI Phone System Built for Houston{' '}
              <strong className="font-medium text-white">HVAC &amp; Plumbing Contractors</strong>
            </p>

            <p
              className="text-sm mb-4 leading-relaxed"
              style={{ color: '#1ECBB5', maxWidth: 500 }}
            >
              We know your industry, your emergencies, and your software.
            </p>

            <p
              className="text-sm mb-4 leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.6)', maxWidth: 500 }}
            >
              Enterprise results without enterprise pricing — built for residential &amp; commercial contractors with 1-5 trucks and 2-10 employees.
            </p>

            <div
              className="mb-9 text-sm leading-relaxed border-l-2 pl-3"
              style={{ borderColor: '#1ECBB5', maxWidth: 500 }}
            >
              <p className="italic mb-3" style={{ color: 'rgba(255,255,255,0.55)' }}>
                While you&apos;re under someone&apos;s crawlspace,{' '}
                <span className="not-italic font-medium" style={{ color: '#1ECBB5' }}>your AI is booking the next job.</span>
              </p>
              <p style={{ color: 'rgba(255,255,255,0.45)' }}>
                Early adopters are locking in their competitive edge right now.{' '}
                <span style={{ color: 'rgba(255,255,255,0.7)' }}>The contractors who move first will capture the market.</span>{' '}
                The ones who wait will spend the next five years trying to catch up.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 flex-wrap mb-12">
              <a
                href="https://calendly.com/growthkeyaisystems-support/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded font-medium text-sm text-white whitespace-nowrap cursor-pointer transition-colors"
                style={{ background: '#F26522', borderRadius: 6 }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#FF7A30'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#F26522'; }}
              >
                Book Free Strategy Call
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded font-light text-sm whitespace-nowrap cursor-pointer transition-all"
                style={{
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.9)',
                  borderRadius: 6,
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = '#1ECBB5';
                  (e.currentTarget as HTMLElement).style.color = '#1ECBB5';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.2)';
                  (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.9)';
                }}
              >
                <MessageSquare className="w-4 h-4" />
                Chat with AI Assistant
              </button>
            </div>

            {/* Quick stats row */}
            <div className="flex flex-wrap gap-6">
              {[
                { val: '100%', label: 'Calls Answered' },
                { val: '48hr', label: 'Go Live' },
                { val: '$150K+', label: 'Revenue Recovered' },
              ].map(s => (
                <div key={s.label}>
                  <div
                    className="text-2xl font-bold"
                    style={{ fontFamily: '\'Bebas Neue\', sans-serif', color: '#1ECBB5', letterSpacing: '0.05em' }}
                  >
                    {s.val}
                  </div>
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — mockup */}
          <div className="hidden lg:block">
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: '1px solid rgba(30,203,181,0.18)' }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20showing%20AI%20assistant%20interface%20with%20appointment%20calendar%20and%20call%20notifications%2C%20HVAC%20and%20plumbing%20service%20scheduling%20app%20mockup%2C%20clean%20tech%20design%2C%20teal%20and%20white%20color%20scheme%2C%20professional%20business%20technology%20visualization&width=600&height=450&seq=hero-phone-hvac-plumb-2&orientation=landscape"
                alt="AI Phone System for HVAC and Plumbing Contractors in Houston"
                className="w-full h-[420px] object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
          <div className="text-xs tracking-widest" style={{ fontFamily: '\'DM Mono\', monospace', color: 'rgba(255,255,255,0.3)' }}>
            SCROLL
          </div>
          <ChevronDown className="w-4 h-4 animate-bounce" style={{ color: '#1ECBB5' }} />
        </div>

        <style>{`
          @keyframes pulse {
            0%,100% { opacity:1 }
            50% { opacity:0.3 }
          }
        `}</style>
      </section>

      {/* ── STATS BAR ── */}
      <section className="py-10 bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { val: '100%', label: 'Calls Answered 24/7' },
              { val: '48hr', label: 'Setup & Go Live' },
              { val: '3x', label: 'More Google Reviews' },
              { val: '$150K+', label: 'Avg. Revenue Recovered' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-4xl font-bold text-white mb-1">{s.val}</div>
                <div className="text-teal-100 text-sm font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY DIFFERENT ── */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Houston HVAC &amp; Plumbing Contractors Choose Us</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We&apos;re not another generic AI platform. We built this specifically for local contractors like you.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { Icon: Zap, title: 'Industry-Specific Training', text: 'Our AI understands furnace failures, burst pipes, AC emergencies, and slab leaks — not generic customer service scripts.' },
              { Icon: Shield, title: 'Built for Small Teams', text: 'Designed for 1-5 truck operations with 2-10 employees doing $300K-$1.5M annually. No bloated enterprise features.' },
              { Icon: Building2, title: 'Residential & Commercial', text: 'Whether you service homes or commercial properties, our AI handles both with the right urgency and terminology.' },
              { Icon: MapPin, title: 'Houston & Harris County', text: 'We know the Houston market — the heat, the humidity, the seasonal demands, and what local customers expect.' },
            ].map(({ Icon, title, text }) => (
              <div key={title} className="bg-white p-8 rounded-xl border border-gray-100 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
                <p className="text-gray-600 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">THE HOUSTON CONTRACTOR&apos;S DILEMMA</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You&apos;re losing $50K-$200K annually from missed calls while you&apos;re on the job fixing someone&apos;s AC or clearing a clogged drain
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { Icon: Users, title: "Can't Find Good Help", text: "Reliable office staff who understand HVAC and plumbing terminology are impossible to find in Houston" },
              { Icon: Phone, title: "Emergency Calls Missed", text: "That burst pipe at midnight or no-AC call in July goes to voicemail — and to your competitor" },
              { Icon: Clock, title: "Peak Season Chaos", text: "Houston summers overwhelm your AC lines while winter freezes flood your plumbing calls" },
              { Icon: MessageSquare, title: "Reviews Slipping", text: "Happy customers forget to leave reviews, unhappy ones don't — and Houston is a competitive market" },
            ].map(({ Icon, title, text }) => (
              <div key={title} className="bg-slate-50 p-8 rounded-xl text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
                <p className="text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">COMPLETE HVAC &amp; PLUMBING COMMUNICATION SUITE</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything Houston contractors need to capture every lead, book every job, and grow your reputation
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {[
              {
                Icon: Phone, title: '24/7 AI Call Handling',
                items: [
                  'Answer every call — 2 AM no-heat emergencies or midnight burst pipes',
                  'Qualify leads with HVAC & plumbing-specific questions',
                  'Book appointments directly into your calendar',
                  'Prioritize emergencies vs. maintenance for both trades',
                ]
              },
              {
                Icon: Smartphone, title: 'Two-Way SMS Communication',
                items: [
                  'Instant text responses to customer inquiries',
                  'Appointment confirmations and reminders',
                  '"On my way" technician notifications',
                  'Follow-up messages after service completion',
                ]
              },
              {
                Icon: Building2, title: 'Google Business Profile Integration',
                items: [
                  'Sync your availability with Google for Houston searches',
                  'Capture leads from "HVAC near me" and "plumber near me"',
                  'Respond to Google messages automatically',
                  'Keep your profile updated and active in Harris County',
                ]
              },
              {
                Icon: ThumbsUp, title: 'Review Generation Automation',
                items: [
                  'Automated review requests after every job',
                  'Smart timing for maximum response rates',
                  'Multi-platform support (Google, Yelp, Facebook)',
                  'Negative feedback intercepted privately',
                ]
              },
              {
                Icon: Zap, title: 'Basic CRM Sync',
                items: [
                  'ServiceTitan, Housecall Pro, Jobber integration',
                  'Automatic lead entry — no double data entry',
                  'Customer history available during calls',
                  'Appointment sync across all platforms',
                ]
              },
              {
                Icon: Globe, title: 'Multilingual Support',
                items: [
                  'English and Spanish call handling — essential for Houston',
                  'Automatic language detection',
                  'Bilingual SMS and text responses',
                  "Serve Houston's entire diverse community",
                ]
              },
            ].map(({ Icon, title, items }) => (
              <div key={title} className="bg-white p-8 rounded-xl border border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <Icon className="h-7 w-7 text-teal-600 mr-3 flex-shrink-0" />
                  {title}
                </h3>
                <ul className="space-y-4">
                  {items.map(item => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-teal-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">GET STARTED IN 3 SIMPLE STEPS</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From sign-up to live calls in as little as 48 hours — no tech skills required
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-20 h-0.5 bg-teal-200" style={{ left: '20%', right: '20%' }} />
            {[
              {
                step: 1,
                img: 'https://readdy.ai/api/search-image?query=Business%20owner%20on%20phone%20having%20a%20consultation%20call%2C%20professional%20office%20setting%2C%20warm%20lighting%2C%20clean%20modern%20desk%20with%20laptop%2C%20friendly%20conversation%2C%20teal%20accent%20colors%20in%20decor%2C%20high%20quality%20professional%20photography&width=400&height=250&seq=step1-strategy-call-hvac&orientation=landscape',
                alt: 'Book your free strategy call with Growth Key AI',
                title: 'Book a Free Strategy Call',
                text: 'We learn about your HVAC or plumbing business, your service area, your software, and your biggest pain points. Takes 30 minutes.',
              },
              {
                step: 2,
                img: 'https://readdy.ai/api/search-image?query=Technical%20setup%20and%20configuration%20on%20computer%20screen%20showing%20AI%20dashboard%20and%20phone%20system%20integration%2C%20modern%20workspace%2C%20clean%20interface%20design%2C%20teal%20and%20white%20color%20scheme%2C%20professional%20technology%20setup%20process&width=400&height=250&seq=step2-custom-setup-hvac&orientation=landscape',
                alt: 'Custom AI setup for your contractor business',
                title: 'We Build Your Custom AI',
                text: 'We train the AI on your specific services, pricing, service area, and CRM. We connect your Google Business Profile, SMS, and review systems.',
              },
              {
                step: 3,
                img: 'https://readdy.ai/api/search-image?query=Happy%20contractor%20business%20owner%20checking%20phone%20with%20notifications%20of%20new%20leads%20and%20booked%20appointments%2C%20successful%20small%20business%2C%20modern%20van%20in%20background%2C%20sunny%20day%2C%20professional%20and%20satisfied%20expression%2C%20teal%20accent%20elements&width=400&height=250&seq=step3-go-live-hvac&orientation=landscape',
                alt: 'Go live and start capturing every lead',
                title: 'Go Live & Never Miss a Call',
                text: 'Your AI starts answering calls, booking jobs, sending texts, and generating reviews — while you focus on the work that pays.',
              },
            ].map(({ step, img, alt, title, text }) => (
              <div key={step} className="relative text-center">
                <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6 relative z-10">{step}</div>
                <img src={img} alt={alt} className="w-full h-[200px] object-cover rounded-xl mb-6" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                <p className="text-gray-600">{text}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a
              href="https://calendly.com/growthkeyaisystems-support/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Start Your Free Strategy Call
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* ── TARGET AUDIENCE ── */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">BUILT FOR HOUSTON CONTRACTORS LIKE YOU</h2>
          <div className="bg-white p-10 rounded-xl max-w-5xl mx-auto">
            <div className="flex items-center justify-center mb-6">
              <Shield className="h-14 w-14 text-teal-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Residential &amp; commercial HVAC and plumbing contractors with 1-5 trucks, 2-10 employees, doing $300K-$1.5M+ annually
            </h3>
            <p className="text-lg text-gray-600 mb-8">
              You can&apos;t afford enterprise pricing, but you need enterprise results. That&apos;s exactly why we built this — right here in Houston for Houston contractors.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {[
                { Icon: Zap, label: 'HVAC Contractors', href: '#services', tags: ['Heating', 'Air Conditioning', 'Refrigeration', 'Indoor Air Quality', 'Commercial HVAC'] },
                { Icon: Wrench, label: 'Plumbing Contractors', href: '#services', tags: ['Drain Cleaning', 'Water Heaters', 'Slab Leaks', 'Repiping', 'Commercial Plumbing'] },
              ].map(({ Icon, label, href, tags }) => (
                <div key={label} className="bg-slate-50 rounded-xl p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                      <Icon className="h-5 w-5 text-teal-600" />
                    </div>
                    <h4 className="font-bold text-gray-900"><a href={href} className="hover:text-teal-600">{label}</a></h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(t => (
                      <span key={t} className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-medium">{t}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICE AREA ── */}
      <section id="service-area" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">SERVING HOUSTON &amp; HARRIS COUNTY</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We proudly support HVAC and plumbing contractors throughout the Greater Houston area
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d443088.3942842498!2d-95.6690399!3d29.8168824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640b8b4488d8501%3A0xca0d02def365053b!2sHouston%2C%20TX!5e0!3m2!1sen!2sus!4v1703000000000!5m2!1sen!2sus"
                width="100%"
                height="400"
                style={{ border: 0, borderRadius: 12 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Growth Key AI Systems Houston Service Area Map"
              />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Areas We Serve</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  'Downtown Houston', 'The Woodlands', 'Sugar Land', 'Katy',
                  'Pearland', 'Cypress', 'Spring', 'Pasadena',
                  'League City', 'Missouri City', 'Baytown', 'Conroe',
                  'Humble', 'Tomball', 'Richmond', 'Bellaire'
                ].map(area => (
                  <div key={area} className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-teal-500 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{area}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-sm mt-6">
                And all surrounding communities throughout Harris County and the Greater Houston metropolitan area.
              </p>
              <a
                href="https://calendly.com/growthkeyaisystems-support/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors mt-6 whitespace-nowrap cursor-pointer"
              >
                See If We Serve Your Area
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">SIMPLE, HONEST PRICING</h2>
            <p className="text-xl text-gray-600">Enterprise results at contractor-friendly prices — for HVAC &amp; plumbing pros</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Starter OS', price: '$697', period: '/month', note: 'Up to 75 calls/month',
                features: ['24/7 AI call answering', 'HVAC & plumbing lead qualification', 'Calendar integration', 'Text notifications', 'Basic CRM sync'],
                highlight: false,
              },
              {
                name: 'Growth OS', price: '$1,297', period: '/month', note: 'Up to 200 calls/month',
                features: ['Everything in Starter OS', 'Two-way SMS communication', 'Google Business Profile integration', 'Review generation automation', 'Call analytics dashboard'],
                highlight: true,
              },
              {
                name: 'Pro OS', price: '$2,497', period: '/month', note: 'Unlimited calls',
                features: ['Everything in Growth OS', 'Outbound follow-up calls', 'Multilingual support (EN/ES)', 'Custom AI training for your business', 'Priority support & advanced reporting'],
                highlight: false,
              },
            ].map(p => (
              <div
                key={p.name}
                className={`bg-white rounded-xl p-8 relative border-2 ${p.highlight ? 'border-teal-500' : 'border-gray-200 hover:border-teal-500'} transition-colors`}
              >
                {p.highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap">Most Popular</span>
                  </div>
                )}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{p.name}</h3>
                  <div className="text-4xl font-bold text-teal-600 mb-2">{p.price}</div>
                  <div className="text-gray-600">{p.period}</div>
                  <div className="mt-3 text-sm text-gray-500">{p.note}</div>
                </div>
                <ul className="space-y-4 mb-8">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-teal-500 mr-3 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="https://calendly.com/growthkeyaisystems-support/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full block text-center py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${p.highlight ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-900 text-white hover:bg-gray-800'}`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">What Houston Contractors Say</h2>
            <p className="text-xl text-gray-600">Real results from real HVAC &amp; plumbing businesses in Harris County</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: 'We went from missing 30% of our calls to capturing every single lead. Our revenue increased by $180K in the first year. The AI actually understands HVAC emergencies — even during Houston\'s brutal summers.',
                name: 'Mike Johnson',
                biz: 'Johnson HVAC Services — Katy, TX',
                img: 'https://readdy.ai/api/search-image?query=Professional%20middle-aged%20male%20HVAC%20contractor%20in%20work%20uniform%2C%20confident%20smile%2C%20clean%20background%2C%20professional%20headshot%20style%2C%20trustworthy%20appearance%2C%20heating%20and%20cooling%20industry%20professional&width=60&height=60&seq=testimonial-hvac-1&orientation=squarish',
              },
              {
                quote: 'As a plumber, I was skeptical an AI could handle emergency drain calls. But it knows the difference between a slow drain and a slab leak. Our Google reviews went from 15 to 92 in four months.',
                name: 'Carlos Rivera',
                biz: 'Rivera Plumbing Solutions — Pearland, TX',
                img: 'https://readdy.ai/api/search-image?query=Professional%20male%20plumber%20contractor%20business%20owner%2C%20experienced%20appearance%2C%20work%20uniform%2C%20clean%20background%2C%20professional%20headshot%20style%2C%20successful%20plumbing%20business%20owner%2C%20trustworthy%20tradesman&width=60&height=60&seq=testimonial-plumb-houston-1&orientation=squarish',
              },
              {
                quote: 'We do both commercial and residential HVAC across Harris County. The bilingual support alone was a game-changer — we stopped losing Spanish-speaking customers to competitors. ROI was immediate.',
                name: 'Sarah Martinez',
                biz: 'Comfort Zone Heating & Air — Spring, TX',
                img: 'https://readdy.ai/api/search-image?query=Professional%20female%20HVAC%20business%20owner%2C%20confident%20expression%2C%20work%20attire%2C%20clean%20background%2C%20professional%20headshot%20style%2C%20successful%20entrepreneur%20appearance%2C%20heating%20cooling%20industry&width=60&height=60&seq=testimonial-hvac-2&orientation=squarish',
              },
            ].map(t => (
              <div key={t.name} className="bg-slate-50 p-8 rounded-xl">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />)}
                </div>
                <p className="text-gray-600 mb-6">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center">
                  <img src={t.img} alt={t.name} className="w-12 h-12 rounded-full object-cover mr-4" />
                  <div>
                    <div className="font-semibold text-gray-900">{t.name}</div>
                    <div className="text-gray-600 text-sm">{t.biz}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="py-20 text-white relative"
        style={{
          backgroundImage: `linear-gradient(rgba(15,23,42,0.9), rgba(15,23,42,0.95)), url('https://readdy.ai/api/search-image?query=Houston%20Texas%20skyline%20at%20dusk%20with%20commercial%20buildings%20and%20residential%20neighborhoods%2C%20warm%20golden%20hour%20lighting%2C%20aerial%20view%20showing%20sprawling%20metropolitan%20area%2C%20professional%20photography%2C%20clean%20composition&width=1920&height=600&seq=cta-houston-contractors-1&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">HOUSTON CONTRACTORS: STOP LOSING MONEY TO MISSED CALLS</h2>
          <p className="text-xl mb-4 max-w-3xl mx-auto text-gray-300">
            Every missed call is a job going to your competitor across Harris County. Let&apos;s fix that.
          </p>
          <p className="text-lg mb-8 text-teal-300 font-medium">
            Book your free 30-minute strategy call and discover how much revenue you&apos;re leaving on the table.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/growthkeyaisystems-support/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              Book Free Strategy Call
              <Calendar className="ml-2 h-5 w-5" />
            </a>
            <a
              href="tel:8326574587"
              className="border-2 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer hover:bg-white hover:text-gray-900"
              style={{ borderColor: '#1ECBB5', color: '#1ECBB5' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#1C2B35'; (e.currentTarget as HTMLElement).style.background = '#1ECBB5'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#1ECBB5'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <Phone className="h-5 w-5" />
              (832) 847-6474
            </a>
            <button
              onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()}
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
            >
              Chat with AI Assistant
              <MessageSquare className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">FREQUENTLY ASKED QUESTIONS</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything Houston HVAC &amp; plumbing contractors want to know before getting started
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</span>
                  {openFaq === index
                    ? <ChevronUp className="h-5 w-5 text-teal-600 flex-shrink-0" />
                    : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  }
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <a
              href="https://calendly.com/growthkeyaisystems-support/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Talk to Us — It&apos;s Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        className="py-12 text-white"
        style={{
          background: '#1C2B35',
          borderTop: '1px solid rgba(30,203,181,0.12)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="flex items-center justify-center rounded w-8 h-8" style={{ background: 'rgba(30,203,181,0.15)', border: '1px solid rgba(30,203,181,0.28)' }}>
                  <Phone className="w-4 h-4" style={{ color: '#1ECBB5' }} />
                </div>
                <span className="text-xl tracking-widest" style={{ fontFamily: '\'Bebas Neue\', sans-serif', letterSpacing: '0.08em' }}>
                  Growth<span style={{ color: '#1ECBB5' }}>Key</span> AI Systems
                </span>
              </div>
              <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
                The AI phone system built exclusively for HVAC &amp; plumbing contractors in Houston and Harris County.
              </p>
              <div className="flex items-center text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
                <MapPin className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#1ECBB5' }} />
                <span>Serving Houston &amp; Harris County, TX</span>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">
                <a href="#services" className="hover:text-teal-400 cursor-pointer">Services</a>
              </h4>
              <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {['24/7 Call Answering', 'Two-Way SMS', 'Review Automation', 'Google Business Integration', 'CRM Sync', 'Multilingual Support'].map(s => <li key={s}>{s}</li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">
                <a href="#pricing" className="hover:text-teal-400 cursor-pointer">Plans</a>
              </h4>
              <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <li>Starter OS - $697/mo</li>
                <li>Growth OS - $1,297/mo</li>
                <li>Pro OS - $2,497/mo</li>
              </ul>
              <h4 className="text-lg font-semibold mt-6 mb-4">
                <a href="#service-area" className="hover:text-teal-400 cursor-pointer">Industries</a>
              </h4>
              <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {['Residential HVAC', 'Commercial HVAC', 'Residential Plumbing', 'Commercial Plumbing'].map(i => <li key={i}>{i}</li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <li>
                  <a href="tel:8326574587" className="hover:text-teal-400 cursor-pointer flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" style={{ color: '#1ECBB5' }} />
                    (832) 847-6474
                  </a>
                </li>
                <li>
                  <a href="https://calendly.com/growthkeyaisystems-support/30min" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 cursor-pointer">Book Strategy Call</a>
                </li>
                <li>
                  <button onClick={() => setIsContactFormOpen(true)} className="hover:text-teal-400 cursor-pointer">Send Message</button>
                </li>
                <li>
                  <button onClick={() => document.querySelector<HTMLElement>('#vapi-widget-floating-button')?.click()} className="hover:text-teal-400 cursor-pointer">Chat with AI</button>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 text-center text-sm" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' }}>
            &copy; 2024 Growth Key AI Systems. All rights reserved. |{' '}
            <a href="https://readdy.ai/?ref=logo" rel="nofollow" className="hover:text-teal-400 ml-1 cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>

      {/* ── MODALS ── */}
      {isStrategyFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            {strategySubmitStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h3>
                <p className="text-gray-600">We&apos;ll contact you soon to schedule your free strategy call.</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Book Your Free Strategy Call</h3>
                {strategySubmitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                    There was an error submitting your request. Please try again.
                  </div>
                )}
                <form id="strategy-call-form" data-readdy-form onSubmit={handleStrategySubmit}>
                  <div className="space-y-4">
                    {[
                      { label: 'Name *', name: 'name', type: 'text', required: true, key: 'name' as const },
                      { label: 'Email *', name: 'email', type: 'email', required: true, key: 'email' as const },
                      { label: 'Phone *', name: 'phone', type: 'tel', required: true, key: 'phone' as const },
                      { label: 'Company Name', name: 'company', type: 'text', required: false, key: 'company' as const },
                    ].map(f => (
                      <div key={f.name}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                        <input
                          type={f.type}
                          name={f.name}
                          required={f.required}
                          value={strategyFormData[f.key]}
                          onChange={e => setStrategyFormData({ ...strategyFormData, [f.key]: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Trade</label>
                      <select name="trade" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer">
                        <option value="">Select your trade</option>
                        {['Residential HVAC', 'Commercial HVAC', 'Residential Plumbing', 'Commercial Plumbing', 'HVAC & Plumbing'].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revenue</label>
                      <select name="revenue" value={strategyFormData.revenue} onChange={e => setStrategyFormData({ ...strategyFormData, revenue: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer">
                        <option value="">Select range</option>
                        {['$300K-$500K', '$500K-$1M', '$1M-$1.5M', '$1.5M+'].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                      <textarea name="message" rows={3} maxLength={500} value={strategyFormData.message} onChange={e => setStrategyFormData({ ...strategyFormData, message: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" placeholder="Tell us about your HVAC or plumbing business challenges..." />
                      <div className="text-xs text-gray-500 mt-1">{strategyFormData.message.length}/500 characters</div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button type="button" onClick={() => { setIsStrategyFormOpen(false); setStrategySubmitStatus('idle'); }} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
                    <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">Book Call</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {isContactFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            {contactSubmitStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600">We&apos;ll get back to you soon.</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
                {contactSubmitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                    There was an error sending your message. Please try again.
                  </div>
                )}
                <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit}>
                  <div className="space-y-4">
                    {[
                      { label: 'Name *', name: 'name', type: 'text', required: true, key: 'name' as const },
                      { label: 'Email *', name: 'email', type: 'email', required: true, key: 'email' as const },
                      { label: 'Phone', name: 'phone', type: 'tel', required: false, key: 'phone' as const },
                    ].map(f => (
                      <div key={f.name}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                        <input type={f.type} name={f.name} required={f.required} value={contactFormData[f.key]} onChange={e => setContactFormData({ ...contactFormData, [f.key]: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                      <textarea name="message" rows={4} maxLength={500} required value={contactFormData.message} onChange={e => setContactFormData({ ...contactFormData, message: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" placeholder="How can we help your HVAC or plumbing business?" />
                      <div className="text-xs text-gray-500 mt-1">{contactFormData.message.length}/500 characters</div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button type="button" onClick={() => { setIsContactFormOpen(false); setContactSubmitStatus('idle'); }} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
                    <button type="submit" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">Send Message</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-6 w-12 h-12 text-white rounded-full flex items-center justify-center cursor-pointer z-40 transition-all"
          style={{ background: '#1ECBB5' }}
          aria-label="Back to top"
        >
          <ChevronUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
